import { useEffect, useRef, useCallback } from "react";
import { useAuth, API } from "../App";
import axios from "axios";
import { toast } from "sonner";

const TRACKING_INTERVAL_MS = 2 * 60 * 1000; // 2 minutes

const LocationTracker = () => {
  const { token, user } = useAuth();
  const intervalRef = useRef(null);
  const lastPositionRef = useRef(null);

  const sendPosition = useCallback(async (position) => {
    if (!token) return;

    const { latitude, longitude } = position.coords;
    
    // Don't send if position hasn't changed significantly (>10m)
    if (lastPositionRef.current) {
      const dist = getDistance(
        lastPositionRef.current.latitude,
        lastPositionRef.current.longitude,
        latitude,
        longitude
      );
      if (dist < 10) return; // Less than 10m movement, skip
    }

    try {
      const response = await axios.post(
        `${API}/location/update`,
        { latitude, longitude },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      lastPositionRef.current = { latitude, longitude };

      // If crossings detected, show notification
      if (response.data.crossings_detected > 0) {
        const crossings = response.data.crossings;
        crossings.forEach((crossing) => {
          toast.success(
            <div>
              <p className="font-bold">🚶 Croisement détecté !</p>
              <p className="text-sm">Vous avez croisé {crossing.user_name} ({crossing.distance}m)</p>
            </div>,
            { duration: 5000 }
          );
        });
      }
    } catch (error) {
      console.error("Error sending position:", error);
    }
  }, [token]);

  const getDistance = (lat1, lon1, lat2, lon2) => {
    const R = 6371000; // Earth radius in meters
    const dLat = ((lat2 - lat1) * Math.PI) / 180;
    const dLon = ((lon2 - lon1) * Math.PI) / 180;
    const a =
      Math.sin(dLat / 2) * Math.sin(dLat / 2) +
      Math.cos((lat1 * Math.PI) / 180) *
        Math.cos((lat2 * Math.PI) / 180) *
        Math.sin(dLon / 2) *
        Math.sin(dLon / 2);
    const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
    return R * c;
  };

  const startTracking = useCallback(() => {
    if (!navigator.geolocation) {
      console.warn("Geolocation not supported");
      return;
    }

    // Get position immediately
    navigator.geolocation.getCurrentPosition(
      sendPosition,
      (error) => console.warn("Geolocation error:", error),
      { enableHighAccuracy: true, timeout: 10000, maximumAge: 60000 }
    );

    // Then update every 2 minutes
    intervalRef.current = setInterval(() => {
      navigator.geolocation.getCurrentPosition(
        sendPosition,
        (error) => console.warn("Geolocation error:", error),
        { enableHighAccuracy: true, timeout: 10000, maximumAge: 60000 }
      );
    }, TRACKING_INTERVAL_MS);
  }, [sendPosition]);

  const stopTracking = useCallback(() => {
    if (intervalRef.current) {
      clearInterval(intervalRef.current);
      intervalRef.current = null;
    }
  }, []);

  useEffect(() => {
    if (token && user) {
      startTracking();
    }

    return () => {
      stopTracking();
    };
  }, [token, user, startTracking, stopTracking]);

  // Handle visibility change - pause when app is in background
  useEffect(() => {
    const handleVisibilityChange = () => {
      if (document.hidden) {
        stopTracking();
      } else if (token && user) {
        startTracking();
      }
    };

    document.addEventListener("visibilitychange", handleVisibilityChange);
    return () => {
      document.removeEventListener("visibilitychange", handleVisibilityChange);
    };
  }, [token, user, startTracking, stopTracking]);

  // This component doesn't render anything
  return null;
};

export default LocationTracker;
